import React, { useState } from 'react';
import { X, MessageCircle, Send, AlertTriangle, Check, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const SUJETS = [
  'Demande de devis',
  'Question sur un produit',
  'Disponibilité / réservation',
  'Mariage',
  'Anniversaire enfant',
  'Entreprise & CE',
  'Zone d\'intervention',
  'Autre',
];

/**
 * ContactModal — Modale de contact universelle
 * Remplace tous les liens "tel:" à travers le site
 * Affiche un avertissement et un formulaire de message
 */
export default function ContactModal({ open, onClose, sujetPrefill = '', sourcePage = 'Site web' }) {
  const [step, setStep] = useState('warning'); // 'warning' | 'form' | 'success'
  const [form, setForm] = useState({
    nom: '',
    email: '',
    sujet: sujetPrefill || 'Demande de devis',
    message: '',
  });
  const [loading, setLoading] = useState(false);

  if (!open) return null;

  const handleClose = () => {
    setStep('warning');
    setForm({ nom: '', email: '', sujet: sujetPrefill || 'Demande de devis', message: '' });
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nom.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error('Merci de remplir tous les champs');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND}/api/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, page_source: sourcePage }),
      });
      if (!res.ok) throw new Error();
      setStep('success');
    } catch {
      toast.error('Erreur lors de l\'envoi. Réessayez.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center p-0 sm:p-4"
      style={{ background: 'rgba(11,30,32,0.65)', backdropFilter: 'blur(6px)' }}
      onClick={handleClose}
    >
      <div
        className="w-full sm:max-w-lg rounded-t-3xl sm:rounded-2xl overflow-hidden"
        style={{
          background: '#FEFAF5',
          boxShadow: '0 32px 80px rgba(11,30,32,0.25)',
          maxHeight: '95vh',
          overflowY: 'auto',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4"
          style={{ background: '#0B1E20', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-xl flex items-center justify-center"
              style={{ background: 'rgba(212,175,55,0.2)' }}>
              <MessageCircle size={16} style={{ color: '#D4AF37' }} />
            </div>
            <p className="font-display font-bold text-sm" style={{ color: '#FFFFFF' }}>
              Nous contacter
            </p>
          </div>
          <button onClick={handleClose}
            className="w-8 h-8 rounded-full flex items-center justify-center"
            style={{ background: 'rgba(255,255,255,0.1)', color: '#FFFFFF' }}>
            <X size={16} />
          </button>
        </div>

        {/* ── ÉTAPE 1 : AVERTISSEMENT ────────────────────────────────── */}
        {step === 'warning' && (
          <div className="p-6 space-y-5">
            {/* Alerte */}
            <div className="flex gap-3 p-4 rounded-2xl"
              style={{ background: 'rgba(212,175,55,0.1)', border: '1px solid rgba(212,175,55,0.3)' }}>
              <AlertTriangle size={20} style={{ color: '#D4AF37', flexShrink: 0, marginTop: 2 }} />
              <div>
                <p className="font-bold text-sm mb-1" style={{ color: '#0B1E20' }}>
                  Il est plus simple de nous contacter via le site !
                </p>
                <p className="text-xs" style={{ color: 'var(--text-muted)', lineHeight: '1.6' }}>
                  En passant par notre formulaire, votre demande est directement enregistrée dans notre système,
                  nous recevons une alerte instantanée et vous obtenez une réponse plus rapide.
                </p>
              </div>
            </div>

            {/* Avantages */}
            <div className="space-y-2">
              {[
                'Réponse garantie sous 24h',
                'Demande enregistrée et suivie',
                'Devis envoyé directement par email',
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2 text-sm" style={{ color: '#0B1E20' }}>
                  <Check size={14} style={{ color: '#1F9D7A', flexShrink: 0 }} />
                  {item}
                </div>
              ))}
            </div>

            {/* Actions */}
            <div className="space-y-2 pt-1">
              <button
                onClick={() => setStep('form')}
                className="w-full flex items-center justify-center gap-2 py-4 rounded-xl font-bold text-sm"
                style={{ background: '#D4AF37', color: '#0B1E20', boxShadow: '0 8px 24px rgba(212,175,55,0.3)' }}
              >
                <MessageCircle size={16} /> Envoyer un message via le site
              </button>
              <a
                href="tel:0756876665"
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-medium text-sm"
                style={{ background: 'transparent', color: 'var(--text-muted)', border: '1px solid var(--border)' }}
              >
                Appeler quand même — 07 56 87 66 65
              </a>
            </div>
          </div>
        )}

        {/* ── ÉTAPE 2 : FORMULAIRE ──────────────────────────────────── */}
        {step === 'form' && (
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <p className="text-xs font-semibold uppercase tracking-widest mb-4"
              style={{ color: 'var(--text-muted)' }}>
              Votre message
            </p>

            {/* Nom */}
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--ink)' }}>
                Votre nom *
              </label>
              <input
                type="text"
                required
                placeholder="Marie Dupont"
                value={form.nom}
                onChange={e => setForm(p => ({ ...p, nom: e.target.value }))}
                className="w-full px-4 py-3 rounded-xl text-sm"
                style={{
                  border: '1.5px solid var(--border)', background: '#FFFFFF',
                  color: 'var(--ink)', outline: 'none',
                  transition: 'border-color 0.2s',
                }}
                onFocus={e => e.target.style.borderColor = '#D4AF37'}
                onBlur={e => e.target.style.borderColor = 'var(--border)'}
              />
            </div>

            {/* Email */}
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--ink)' }}>
                Email *
              </label>
              <input
                type="email"
                required
                placeholder="marie@email.fr"
                value={form.email}
                onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                className="w-full px-4 py-3 rounded-xl text-sm"
                style={{
                  border: '1.5px solid var(--border)', background: '#FFFFFF',
                  color: 'var(--ink)', outline: 'none',
                  transition: 'border-color 0.2s',
                }}
                onFocus={e => e.target.style.borderColor = '#D4AF37'}
                onBlur={e => e.target.style.borderColor = 'var(--border)'}
              />
            </div>

            {/* Sujet */}
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--ink)' }}>
                Sujet *
              </label>
              <div className="relative">
                <select
                  value={form.sujet}
                  onChange={e => setForm(p => ({ ...p, sujet: e.target.value }))}
                  className="w-full px-4 py-3 rounded-xl text-sm appearance-none pr-10"
                  style={{
                    border: '1.5px solid var(--border)', background: '#FFFFFF',
                    color: 'var(--ink)', outline: 'none',
                  }}
                >
                  {SUJETS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none"
                  style={{ color: 'var(--text-muted)' }} />
              </div>
            </div>

            {/* Message */}
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--ink)' }}>
                Votre message *
              </label>
              <textarea
                required
                rows={4}
                placeholder="Décrivez votre événement : date, lieu, type d'animation souhaitée, nombre d'invités…"
                value={form.message}
                onChange={e => setForm(p => ({ ...p, message: e.target.value }))}
                className="w-full px-4 py-3 rounded-xl text-sm resize-none"
                style={{
                  border: '1.5px solid var(--border)', background: '#FFFFFF',
                  color: 'var(--ink)', outline: 'none',
                  transition: 'border-color 0.2s',
                  lineHeight: '1.6',
                }}
                onFocus={e => e.target.style.borderColor = '#D4AF37'}
                onBlur={e => e.target.style.borderColor = 'var(--border)'}
              />
            </div>

            {/* Boutons */}
            <div className="flex gap-3 pt-1">
              <button
                type="button"
                onClick={() => setStep('warning')}
                className="px-4 py-3 rounded-xl text-sm font-medium"
                style={{ background: 'var(--bg)', color: 'var(--text-muted)', border: '1px solid var(--border)' }}>
                ← Retour
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-sm"
                style={{
                  background: loading ? 'rgba(212,175,55,0.5)' : '#D4AF37',
                  color: '#0B1E20',
                  cursor: loading ? 'not-allowed' : 'pointer',
                }}>
                {loading ? (
                  <span>Envoi en cours…</span>
                ) : (
                  <><Send size={15} /> Envoyer le message</>
                )}
              </button>
            </div>
          </form>
        )}

        {/* ── ÉTAPE 3 : SUCCÈS ──────────────────────────────────────── */}
        {step === 'success' && (
          <div className="p-8 text-center space-y-4">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{ background: 'rgba(31,157,122,0.12)' }}>
              <Check size={28} style={{ color: '#1F9D7A' }} />
            </div>
            <h3 className="font-display text-xl font-bold" style={{ color: '#0B1E20' }}>
              Message envoyé !
            </h3>
            <p className="text-sm" style={{ color: 'var(--text-muted)', lineHeight: '1.7' }}>
              Merci <strong>{form.nom}</strong> ! Votre demande a bien été reçue.<br />
              Nous vous répondons sous <strong>24h</strong> à l'adresse <strong>{form.email}</strong>.
            </p>
            <div className="p-3 rounded-xl text-xs font-medium"
              style={{ background: 'rgba(31,157,122,0.08)', color: '#1F9D7A' }}>
              💡 Vous pouvez aussi composer votre devis directement en ligne via le configurateur
            </div>
            <button
              onClick={handleClose}
              className="w-full py-3 rounded-xl font-bold text-sm mt-2"
              style={{ background: '#0B1E20', color: '#FFFFFF' }}>
              Fermer
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
